import { useState, useEffect } from 'react';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Filter, FilterX, ArrowDownAZ, ArrowUpAZ, ArrowDown10, ArrowUp10, Search, IdCard } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { FilterOptions } from '@/services/clothingService';

// Import the categories structure from ItemForm
const categories = [
  {
    name: 'Tops',
    subcategories: ['Tees', 'Hoodies', 'Jackets', 'Vests', 'Sweaters', 'Jerseys']
  },
  {
    name: 'Bottoms',
    subcategories: ['Jeans', 'Cargos', 'Sweatpants', 'Shorts']
  },
  {
    name: 'Accessories',
    subcategories: [
      'Wallets', 'Belts', 'Glasses', 'Gloves', 'Hats', 'Beanies', 
      'Facemasks', 'Keychains', 'Socks', 'Underwear', 'Phone Cases', 
      'Air Pod Cases', 'Ties'
    ]
  },
  {
    name: 'Jewelry',
    subcategories: ['Bracelets', 'Necklaces', 'Rings', 'Watches']
  },
  {
    name: 'Bags',
    subcategories: ['Shoulder Bags', 'Backpacks', 'Duffle Bags', 'Tote Bags']
  },
  {
    name: 'Tech',
    subcategories: []
  },
  {
    name: 'Room Decorations',
    subcategories: ['Figures', 'Pillows', 'Plushes', 'Rugs', 'Posters', 'Lego', 'Other']
  }
];

// Generate a flattened list of all categories and subcategories
const allCategoryOptions = [
  ...categories.map(cat => cat.name),
  ...categories.flatMap(cat => cat.subcategories)
];

type SortOption = 'name_asc' | 'name_desc' | 'price_asc' | 'price_desc' | 'id_asc' | 'id_desc' | '';

interface FilterPanelProps {
  onFilterChange: (filters: FilterOptions) => void;
  onSortChange: (sort: SortOption) => void;
  onReset: () => void;
  activeFilterCount: number;
}

const FilterPanel = ({ onFilterChange, onSortChange, onReset, activeFilterCount }: FilterPanelProps) => {
  const [category, setCategory] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [itemId, setItemId] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('');
  const [dbCategories, setDbCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const { data, error } = await supabase
          .from('db')
          .select('*');
        
        if (error) {
          console.error('Error fetching data:', error);
          return;
        }
        
        if (data) {
          // Get all categories from the database
          let allDbCategories: string[] = [];
          data.forEach(item => {
            if (item['category[0]']) allDbCategories.push(item['category[0]']);
            if (item['category[1]']) allDbCategories.push(item['category[1]']);
            if (item['category[2]']) allDbCategories.push(item['category[2]']);
          });
          
          const uniqueDbCategories = Array.from(
            new Set(allDbCategories.filter(Boolean))
          ).sort();
          
          const uniqueBrands = Array.from(
            new Set(data.map(item => item.brand).filter(Boolean))
          ).sort();
          
          setDbCategories(uniqueDbCategories as string[]);
          setBrands(uniqueBrands as string[]);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleApplyFilters = () => {
    const filters: FilterOptions = {};
    
    if (category && category !== '_all') filters.category = category;
    if (brand && brand !== '_all') filters.brand = brand;
    
    // Validate and set price range
    const minPriceNum = parseFloat(minPrice);
    const maxPriceNum = parseFloat(maxPrice);
    if (!isNaN(minPriceNum) && minPriceNum >= 0) {
      filters.minPrice = minPriceNum;
    }
    if (!isNaN(maxPriceNum) && maxPriceNum >= 0) {
      filters.maxPrice = maxPriceNum;
    }
    // Validate that min price is not greater than max price
    if (filters.minPrice !== undefined && filters.maxPrice !== undefined && filters.minPrice > filters.maxPrice) {
      alert('Minimum price cannot be greater than maximum price');
      return;
    }
    
    if (itemId) {
      const itemIdNum = parseInt(itemId, 10);
      if (!isNaN(itemIdNum) && itemIdNum > 0) {
        filters.itemId = itemIdNum;
      }
    }
    
    onFilterChange(filters);
    onSortChange(sortBy);
    setIsOpen(false);
  };

  const handleReset = () => {
    setCategory('');
    setBrand('');
    setMinPrice('');
    setMaxPrice('');
    setItemId('');
    setSortBy('');
    onReset();
    setIsOpen(false);
  };

  // Combine predefined categories with those found in the database
  const combinedCategories = [...new Set([...allCategoryOptions, ...dbCategories])].sort();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Filter className="h-4 w-4 mr-2" />
          Filter
          {activeFilterCount > 0 && (
            <Badge 
              variant="secondary" 
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            Filters & Sorting
          </SheetTitle>
        </SheetHeader>
        <div className="py-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium flex items-center">
              <IdCard className="h-4 w-4 mr-2" />
              Item ID
            </h3>
            <div className="grid gap-2">
              <Input
                id="item-id"
                type="number"
                min="1"
                placeholder="Enter item ID"
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Categories</h3>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Categories</SelectItem>
                {/* Main Categories Group */}
                <SelectItem value="_group_header" disabled className="font-semibold text-sm opacity-50">
                  Main Categories
                </SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                ))}
                
                {/* Subcategories Group */}
                <SelectItem value="_group_divider" disabled>
                  <Separator className="my-1" />
                </SelectItem>
                <SelectItem value="_subcat_header" disabled className="font-semibold text-sm opacity-50">
                  Subcategories
                </SelectItem>
                {categories.flatMap(cat => 
                  cat.subcategories.map(subcat => (
                    <SelectItem key={subcat} value={subcat}>{subcat}</SelectItem>
                  ))
                )}
                
                {/* Other Categories from DB */}
                {dbCategories.filter(cat => !combinedCategories.includes(cat)).length > 0 && (
                  <>
                    <SelectItem value="_other_divider" disabled>
                      <Separator className="my-1" />
                    </SelectItem>
                    <SelectItem value="_other_header" disabled className="font-semibold text-sm opacity-50">
                      Other Categories
                    </SelectItem>
                    {dbCategories
                      .filter(cat => !combinedCategories.includes(cat))
                      .map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))
                    }
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Brands</h3>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Brands</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Price Range</h3>
            <div className="flex items-center space-x-2">
              <div className="grid gap-2">
                <Label htmlFor="min-price">Min</Label>
                <Input
                  id="min-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="max-price">Max</Label>
                <Input
                  id="max-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="1000.00"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Sort By</h3>
            <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
              <SelectTrigger>
                <SelectValue placeholder="Default order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_default">Default</SelectItem>
                <SelectItem value="id_asc">
                  <div className="flex items-center">
                    <ArrowUp10 className="mr-2 h-4 w-4" />
                    ID (Low to High)
                  </div>
                </SelectItem>
                <SelectItem value="id_desc">
                  <div className="flex items-center">
                    <ArrowDown10 className="mr-2 h-4 w-4" />
                    ID (High to Low)
                  </div>
                </SelectItem>
                <SelectItem value="name_asc">
                  <div className="flex items-center">
                    <ArrowDownAZ className="mr-2 h-4 w-4" />
                    Name (A-Z)
                  </div>
                </SelectItem>
                <SelectItem value="name_desc">
                  <div className="flex items-center">
                    <ArrowUpAZ className="mr-2 h-4 w-4" />
                    Name (Z-A)
                  </div>
                </SelectItem>
                <SelectItem value="price_asc">
                  <div className="flex items-center">
                    <ArrowUp10 className="mr-2 h-4 w-4" />
                    Low to High
                  </div>
                </SelectItem>
                <SelectItem value="price_desc">
                  <div className="flex items-center">
                    <ArrowDown10 className="mr-2 h-4 w-4" />
                    High to Low
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handleReset} className="flex items-center">
              <FilterX className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handleApplyFilters}>Apply Filters</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FilterPanel;
